/**
 * Which optional pages the current site has. A page that is off is not routed
 * (App.tsx), not prerendered or sitemapped (vite.config.ts), not redirected to
 * (scripts/generate_redirects.cjs) and not linked from navigation.
 *
 * A page listed in the site's data/merged-pages.json (folded into another page
 * and 301'd there) is off too, e.g. Japan's /opening-dates -> /season-calendar.
 *
 * Relative imports only (besides @site): vite.config.ts bundles this file.
 */
import { SITE } from './index';
import mergedPages from '@site/data/merged-pages.json';

const flagship = SITE.routes.flagship;

/** Paths (no language prefix) folded into another page on this site. */
const MERGED = new Set(Object.keys((mergedPages as { pages: Record<string, string> }).pages));
/** The path while the site has the page and has not merged it away, else null. */
const live = (on: boolean | undefined, path: string): string | null => (on && !MERGED.has(path) ? path : null);

/** Path (without language prefix, no trailing slash) of each optional page, or null when the site lacks it. */
export const OPTIONAL_PAGES = {
  bestResorts: live(SITE.features.bestResorts, `/${SITE.routes.bestResorts}`),
  winterTraffic: live(SITE.features.winterTraffic, '/winter-traffic'),
  seasonCalendar: live(SITE.features.seasonCalendar, '/season-calendar'),
  openingDates: live(SITE.features.seasonCalendar, '/opening-dates'),
  snowForecast: live(SITE.features.snowForecast, '/snow-forecast'),
  monthlySnowForecast: live(SITE.features.monthlySnowForecast, '/monthly-snow-forecast'),
  racetracks: live(SITE.features.racetracks, '/racetrack'),
  flagship: live(!!flagship, `/${flagship?.slug}`),
  flagshipGuide: live(!!flagship, `/${flagship?.slug}/travel-guide`),
  flagshipFaq: live(!!flagship, `/${flagship?.slug}/faq`),
} as const;

export type OptionalPage = keyof typeof OPTIONAL_PAGES;

/** True when the site has the page. */
export const hasPage = (page: OptionalPage): boolean => OPTIONAL_PAGES[page] !== null;

/** The page's path ('' when the site lacks it; callers should check hasPage first). */
export const pagePath = (page: OptionalPage): string => OPTIONAL_PAGES[page] ?? '';

/** Every optional page path a site lacks, for filtering link lists. */
export const DISABLED_PATHS = new Set<string>(
  [
    ['/best-ski-resorts-japan', SITE.features.bestResorts && SITE.routes.bestResorts === 'best-ski-resorts-japan'],
    ['/winter-traffic', hasPage('winterTraffic')],
    ['/season-calendar', hasPage('seasonCalendar')],
    ['/opening-dates', hasPage('openingDates')],
    ['/snow-forecast', hasPage('snowForecast')],
    ['/monthly-snow-forecast', hasPage('monthlySnowForecast')],
    ['/furano', flagship?.slug === 'furano'],
  ]
    .filter(([, on]) => !on)
    .map(([path]) => path as string)
);

/** False for links to a page the current site does not have (accepts /path, /path/ and /:lang/path/). */
export function isEnabledPath(href: string): boolean {
  const bare = href.replace(/^\/(en|ja|ko|zh)(?=\/|$)/, '').replace(/[?#].*$/, '').replace(/\/+$/, '') || '/';
  for (const disabled of DISABLED_PATHS) {
    if (bare === disabled || bare.startsWith(`${disabled}/`)) return false;
  }
  return true;
}
